


























































































import {
  computed,
  onMounted,
  reactive,
  ref,
  watch
} from '@nuxtjs/composition-api';
import type { ValidationObserver } from 'vee-validate';
import {
  useCurrentUser,
  usePDPStockNotifications
} from '@vue-storefront/novulo/';

type Element = {
  key: string;
  value_string: string;
  value_string_area?: string;
};

export default {
  name: 'BuySectionDeliverySubscriptionDrawer',
  props: {
    isVisible: { type: Boolean, default: false },
    productId: { type: Number, default: null }
  },
  setup(props) {
    const {
      getSubscriptionStatus,
      getContainerTranslations,
      subscribeToNotifications
    } = usePDPStockNotifications();
    const { currentUser } = useCurrentUser();

    const isProcessing = ref(false);
    const isConfirmed = ref(false);
    const isAlreadySubscribed = ref(false);
    const isAuthenticated = ref(Boolean(currentUser.value));

    const containerTranslations = ref([]);

    const tForm = computed(() => {
      const formFieldsArray =
        containerTranslations.value.find(
          (element) => element.code === 'instock_form'
        )?.elements[0]?.custom_fields || [];
      const formFields = {};
      formFieldsArray.forEach(
        (element: Element) => (formFields[element.key] = element.value_string)
      );

      return formFields;
    });

    const tConfirmation = computed(() => {
      const formFieldsArray =
        containerTranslations.value.find(
          (element) => element.code === 'instock_confirmation'
        )?.elements[0]?.custom_fields || [];
      const formFields = {};
      formFieldsArray.forEach(
        (element: Element) =>
          (formFields[element.key] =
            element.value_string || element.value_string_area)
      );

      return formFields;
    });

    const tAlreadySubscribed = computed(() => {
      const formFieldsArray =
        containerTranslations.value.find(
          (element) => element.code === 'instock_already_subscribed'
        )?.elements[0]?.custom_fields || [];
      const formFields = {};
      formFieldsArray.forEach(
        (element: Element) =>
          (formFields[element.key] =
            element.value_string || element.value_string_area)
      );

      return formFields;
    });

    const title = computed(() => {
      const state = isConfirmed.value
        ? tConfirmation
        : isAlreadySubscribed.value
        ? tAlreadySubscribed
        : tForm;
      return (state.value as { Title: string }).Title;
    });

    const form = ref<InstanceType<typeof ValidationObserver>>(null);

    const model = reactive({
      first_name: '',
      surname_prefix: '',
      surname: '',
      email_address: '',
      telephone: ''
    });

    onMounted(async () => {
      containerTranslations.value = await getContainerTranslations();
    });

    const formSuccess = ref(null);

    const SUBSCRIBED_FEEDBACK_TIME_MS = 2000;
    const subscribe = async (model) => {
      isProcessing.value = true;
      await subscribeToNotifications({
        ...model,
        product: props.productId
      }).then(() => {
        formSuccess.value = true;
        isConfirmed.value = true;
        isAlreadySubscribed.value = true;
        setTimeout(() => {
          isProcessing.value = false;
          formSuccess.value = null;
        }, SUBSCRIBED_FEEDBACK_TIME_MS);
      });
    };

    const submitForm = () => {
      form.value.validate().then(async (success) => {
        if (success) {
          isProcessing.value = true;
          const { is_already_subscribed } = await getSubscriptionStatus(
            props.productId,
            model.email_address
          );
          if (is_already_subscribed) {
            isProcessing.value = false;
            return (isAlreadySubscribed.value = is_already_subscribed);
          }

          subscribe(model);
        }
      });
    };

    watch(
      () => props.isVisible,
      async (next) => {
        if (next && isAuthenticated.value) {
          isProcessing.value = true;
          const { is_already_subscribed } = await getSubscriptionStatus(
            props.productId
          );
          isAlreadySubscribed.value = is_already_subscribed;
          if (!is_already_subscribed) {
            await subscribe({
              first_name: currentUser.value.contact.first_name,
              surname_prefix: null,
              surname: currentUser.value.contact.full_surname,
              email_address: currentUser.value.email,
              telephone: null
            });
          }
          isProcessing.value = false;
        }
      }
    );

    return {
      isProcessing,
      isConfirmed,
      isAlreadySubscribed,
      tForm,
      tConfirmation,
      tAlreadySubscribed,
      currentUser,
      title,
      form,
      model,
      formSuccess,
      submitForm
    };
  }
};
